import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, CircularProgress, TextField, IconButton, Grid
} from '@mui/material';
import { Home, Print, HourglassBottom } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const todayStr = () => new Date().toISOString().split('T')[0];

const BUCKET_COLOR = { '0-30': '#2e7d32', '31-60': '#1565c0', '61-90': '#f57c00', '90+': '#c62828' };

const InventoryAging = () => {
  const navigate = useNavigate();
  const [asOf,    setAsOf]    = useState(todayStr());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/payables/inventory/aging`, {
        headers: hdrs(), params: { asOf }
      });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to compute aging');
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buckets = data?.buckets || ['0-30', '31-60', '61-90', '90+'];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } }`}</style>

      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
            <Box display="flex" alignItems="center">
              <HourglassBottom sx={{ mr: 1, color: '#6a1b9a' }} />
              <Typography variant="h5" sx={{ color: '#6a1b9a' }}>Inventory Aging</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => window.print()} title="Print" disabled={!data}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Print />
              </IconButton>
              <IconButton onClick={() => navigate('/supply-chain')} title="Supply Chain Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            On-hand stock value grouped by age (days since received), using FIFO layers.
            Slow-moving stock shows up in the older buckets.
          </Typography>

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2} className="no-print">
            <TextField label="As of date" type="date" size="small" value={asOf}
              onChange={e => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }} />
            <IconButton onClick={run} disabled={loading} color="primary"
              sx={{ bgcolor: '#6a1b9a15' }}>
              {loading ? <CircularProgress size={20} /> : <HourglassBottom />}
            </IconButton>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {data && (
            <>
              <Grid container spacing={2} mb={2}>
                {buckets.map(b => (
                  <Grid item xs={6} sm={3} key={b}>
                    <Card elevation={2} sx={{ borderLeft: `4px solid ${BUCKET_COLOR[b]}` }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="h6" fontWeight={800} color={BUCKET_COLOR[b]} lineHeight={1}>
                          {sym()} {fmt(data.totals[b])}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{b} days</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {data.rows.length === 0 ? <Typography>No stock on hand as of this date.</Typography> : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Item No</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                        <TableCell align="right"><strong>Qty</strong></TableCell>
                        {buckets.map(b => (
                          <TableCell key={b} align="right" sx={{ color: BUCKET_COLOR[b] }}>
                            <strong>{b}</strong>
                          </TableCell>
                        ))}
                        <TableCell align="right"><strong>Total Value</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.rows.map(r => (
                        <TableRow key={r.itemNo} hover>
                          <TableCell><strong>{r.itemNo}</strong></TableCell>
                          <TableCell>{r.itemName}</TableCell>
                          <TableCell align="right">{fmt(r.qty)}</TableCell>
                          {buckets.map(b => (
                            <TableCell key={b} align="right"
                              sx={{ color: r.buckets[b].value > 0 ? BUCKET_COLOR[b] : 'text.disabled' }}>
                              {r.buckets[b].value > 0 ? fmt(r.buckets[b].value) : '—'}
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(r.value)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 800 }}>Totals</TableCell>
                        {buckets.map(b => (
                          <TableCell key={b} align="right" sx={{ fontWeight: 800, color: BUCKET_COLOR[b] }}>
                            {fmt(data.totals[b])}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 800 }}>{sym()} {fmt(data.totals.value)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InventoryAging;
